import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Container,
    Grid,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import MaterialTable from "material-table";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";

import { useAuth } from "../hooks/useAuth";
import { Icons } from "../utils/Icon";
import { CustomStyles } from "../utils/Styled";
import LinkMillionProduct from "../components/forms/products/LinkMillionProduct";
import CreateProductForm from "../components/forms/products/Create";

export const MillionProductMappingPage = () => {
    const { logout } = useAuth();
    const classes = CustomStyles;
    const searchInputRef = useRef(null);

    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [viewMode, setViewMode] = useState("million");
    const [filter, setFilter] = useState({ search: "", status: "all", country_id: "" });
    const [syncStatus, setSyncStatus] = useState({ state: null, millionProductTotal: 0, crmProductTotal: 0, linkedProductTotal: 0 });

    const [crmProducts, setCrmProducts] = useState([]);
    const [userCountry, setUserCountry] = useState([]);

    const [autoPairLoading, setAutoPairLoading] = useState(false);
    const [rowActionLoading, setRowActionLoading] = useState({});

    const [editingCode, setEditingCode] = useState(null);
    const [selectedLinkProduct, setSelectedLinkProduct] = useState(null);
    const [editingCrmProductId, setEditingCrmProductId] = useState(null);
    const [selectedMillionLink, setSelectedMillionLink] = useState(null);
    const [millionOptions, setMillionOptions] = useState([]);
    const [millionSearch, setMillionSearch] = useState("");

    const [openCreate, setOpenCreate] = useState(false);
    const [selectedMillionRow, setSelectedMillionRow] = useState(null);

    const countryOptions = useMemo(() => {
        return (userCountry || []).filter((country) => country.country_id === 158 || country.country_id === 198);
    }, [userCountry]);

    const filteredCrmProducts = useMemo(() => {
        if (!filter.country_id) {
            return [];
        }

        return (crmProducts || []).filter((product) => Number(product.country_id) === Number(filter.country_id));
    }, [crmProducts, filter.country_id]);

    const actionButtonSx = {
        width: 100,
        minWidth: 100,
        height: 55,
        fontSize: "0.95rem",
    };

    const withRowLoading = (key, loading) => {
        setRowActionLoading((prev) => ({ ...prev, [key]: loading }));
    };

    const fetchCountryData = useCallback(async () => {
        const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/users/access-country");
        setUserCountry(response.data.data.countries || []);
    }, []);

    const fetchCrmProducts = useCallback(async () => {
        const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/products/select");
        setCrmProducts(response.data.data.products || []);
    }, []);

    const fetchData = useCallback(async () => {
        try {
            if (!filter.country_id) {
                return;
            }

            const endpoint = viewMode === "million" ? "api/v1/million-products" : "api/v1/million-products/crm-products";

            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + endpoint, {
                params: {
                    page: page + 1,
                    perPage,
                    country_id: filter.country_id,
                    search: filter.search,
                    status: filter.status,
                },
            });

            const rows = viewMode === "million" ? response.data.data.millionProducts : response.data.data.crmProducts;
            setData(rows || []);
            setCount(response.data.data.total || 0);
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }
            Swal.fire({
                title: "Error",
                text: error?.response?.data?.message || "Failed to fetch Million products.",
                icon: "error",
            });
        }
    }, [page, perPage, filter, logout, viewMode]);

    const fetchMillionOptions = useCallback(async () => {
        try {
            if (!filter.country_id) {
                return;
            }

            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/million-products", {
                params: {
                    page: 1,
                    perPage: 200,
                    country_id: filter.country_id,
                    search: millionSearch,
                    status: "all",
                },
            });

            const rawOptions = response.data.data.millionProducts || [];

            // Keep one option per canonical Million code so Autocomplete keys stay unique.
            const uniqueOptions = Object.values(
                rawOptions.reduce((acc, item) => {
                    if (!item?.code) {
                        return acc;
                    }
                    if (!acc[item.code]) {
                        acc[item.code] = item;
                    }
                    return acc;
                }, {})
            );

            setMillionOptions(uniqueOptions);
        } catch (error) {
            console.log(error);
        }
    }, [millionSearch, filter.country_id]);

    const fetchSyncStatus = useCallback(async () => {
        try {
            if (!filter.country_id) {
                return;
            }

            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/million-products/sync-status", {
                params: {
                    country_id: filter.country_id,
                },
            });
            setSyncStatus(response.data.data || { state: null, millionProductTotal: 0, crmProductTotal: 0, linkedProductTotal: 0 });
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }
            Swal.fire({
                title: "Error",
                text: error?.response?.data?.message || "Failed to fetch sync status.",
                icon: "error",
            });
        }
    }, [logout, filter.country_id]);

    const refreshAll = useCallback(async () => {
        await Promise.all([fetchData(), fetchSyncStatus()]);
    }, [fetchData, fetchSyncStatus]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchSyncStatus();
        fetchCrmProducts();
        fetchCountryData();
    }, [fetchSyncStatus, fetchCrmProducts, fetchCountryData]);

    useEffect(() => {
        if (filter.country_id !== "") {
            return;
        }

        if (!countryOptions.length) {
            return;
        }

        setFilter((prev) => ({ ...prev, country_id: countryOptions[0].country_id }));
    }, [countryOptions, filter.country_id]);

    useEffect(() => {
        if (viewMode === "crm" && editingCrmProductId !== null) {
            fetchMillionOptions();
        }
    }, [viewMode, editingCrmProductId, fetchMillionOptions]);

    useEffect(() => {
        setEditingCode(null);
        setSelectedLinkProduct(null);
    }, [filter.country_id]);

    const handleFilterChange = (event) => {
        setPage(0);
        const { name, value } = event.target;
        setFilter((prev) => ({
            ...prev,
            [name]: name === "country_id" ? Number(value) : value,
        }));
    };

    const handleAutoPair = async () => {
        const result = await Swal.fire({
            title: "Run Auto-Pair?",
            text: "This will automatically link Million products and CRM products that have the same product code.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Run Auto-Pair",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            setAutoPairLoading(true);
            const response = await axios.post(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/million-products/auto-pair");
            Swal.fire({
                title: "Auto-Pair Completed",
                text: `Affected rows: ${response.data.data.affectedRows || 0}`,
                icon: "success",
                showConfirmButton: false,
                timer: 1800,
            });
            await refreshAll();
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error?.response?.data?.message || "Failed to run auto-pair.",
                icon: "error",
            });
        } finally {
            setAutoPairLoading(false);
        }
    };

    const handleSaveLink = async (row) => {
        if (!selectedLinkProduct?.product_id) {
            return;
        }

        const key = `link-${row.code}`;
        try {
            withRowLoading(key, true);
            await axios.patch(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/products/${selectedLinkProduct.product_id}/million-link`, {
                million_product_code: row.code,
            });

            Swal.fire({
                title: "Success",
                text: "Million product linked successfully",
                icon: "success",
                showConfirmButton: false,
                timer: 1600,
            });

            setEditingCode(null);
            setSelectedLinkProduct(null);
            await refreshAll();
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error?.response?.data?.message || "Failed to link million product.",
                icon: "error",
            });
        } finally {
            withRowLoading(key, false);
        }
    };

    const handleSaveLinkFromCrm = async (row) => {
        if (!selectedMillionLink?.code) {
            return;
        }

        const key = `link-crm-${row.product_id}`;
        try {
            withRowLoading(key, true);
            await axios.patch(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/products/${row.product_id}/million-link`, {
                million_product_code: selectedMillionLink.code,
            });

            Swal.fire({
                title: "Success",
                text: "Million product linked successfully",
                icon: "success",
                showConfirmButton: false,
                timer: 1600,
            });

            setEditingCrmProductId(null);
            setSelectedMillionLink(null);
            setMillionSearch("");
            await refreshAll();
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error?.response?.data?.message || "Failed to link million product.",
                icon: "error",
            });
        } finally {
            withRowLoading(key, false);
        }
    };

    const handleUnlink = async (row) => {
        const linkedCode = row.code || row.million_product_code || "-";
        const result = await Swal.fire({
            title: "Unlink Million Product",
            text: `Unlink ${linkedCode} from ${row.product_name}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Unlink",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#d33",
        });

        if (!result.isConfirmed) {
            return;
        }

        const key = `unlink-${row.product_id}`;
        try {
            withRowLoading(key, true);
            await axios.delete(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/products/${row.product_id}/million-link`);

            Swal.fire({
                title: "Success",
                text: "Million product unlinked successfully",
                icon: "success",
                showConfirmButton: false,
                timer: 1600,
            });

            await refreshAll();
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error?.response?.data?.message || "Failed to unlink million product.",
                icon: "error",
            });
        } finally {
            withRowLoading(key, false);
        }
    };

    const syncStatusChip = useMemo(() => {
        const status = syncStatus?.state?.last_status;

        if (status === "success") {
            return <Chip label="SUCCESS" color="success" size="small" />;
        }
        if (status === "failed") {
            return <Chip label="FAILED" color="error" size="small" />;
        }
        if (status === "partial") {
            return <Chip label="PARTIAL" color="warning" size="small" />;
        }
        return <Chip label="NO STATUS" size="small" />;
    }, [syncStatus]);

    const columns =
        viewMode === "million"
            ? [
                  { title: "Million Code", field: "code", cellStyle: { width: 140, maxWidth: 140 }, headerStyle: { width: 140, maxWidth: 140 } },
                  { title: "Million Description", field: "description" },
                  {
                      title: "Million Price",
                      field: "price",
                      cellStyle: { width: 130, maxWidth: 130 },
                      headerStyle: { width: 130, maxWidth: 130 },
                      render: (rowData) => (rowData.price === null || rowData.price === undefined ? "-" : Number(rowData.price).toFixed(2)),
                  },
                  {
                      title: "Updated At",
                      field: "updated_at",
                      cellStyle: { width: 190, maxWidth: 190 },
                      headerStyle: { width: 190, maxWidth: 190 },
                      render: (rowData) => (rowData.updated_at ? moment(rowData.updated_at).format("YYYY-MM-DD HH:mm:ss") : "-"),
                  },
                  {
                      title: "Link Status",
                      field: "product_id",
                      cellStyle: { width: 120, maxWidth: 120 },
                      headerStyle: { width: 120, maxWidth: 120 },
                      render: (rowData) => (rowData.product_id ? <Chip label="LINKED" color="success" size="small" /> : <Chip label="UNLINKED" color="default" size="small" />),
                  },
                  { title: "CRM Product", field: "product_name", render: (rowData) => rowData.product_name || "-" },
                  { title: "CRM Description", field: "product_description", render: (rowData) => rowData.product_description || "-" },
                  {
                      title: "Actions",
                      sorting: false,
                      filtering: false,
                      render: (rowData) => {
                          if (editingCode === rowData.code) {
                              return (
                                  <LinkMillionProduct
                                      products={filteredCrmProducts}
                                      value={selectedLinkProduct}
                                      onChange={setSelectedLinkProduct}
                                      onSave={() => handleSaveLink(rowData)}
                                      onCancel={() => {
                                          setEditingCode(null);
                                          setSelectedLinkProduct(null);
                                      }}
                                      loading={!!rowActionLoading[`link-${rowData.code}`]}
                                  />
                              );
                          }

                          if (rowData.product_id) {
                              return (
                                  <ButtonGroup>
                                      <Button
                                          variant="outlined"
                                          color="primary"
                                          sx={actionButtonSx}
                                          onClick={() => {
                                              setEditingCode(rowData.code);
                                              setSelectedLinkProduct(
                                                  rowData.product_id
                                                      ? {
                                                            product_id: rowData.product_id,
                                                            product_description: rowData.product_description || rowData.product_name,
                                                            country_id: rowData.country_id,
                                                        }
                                                      : null
                                              );
                                          }}
                                      >
                                          Change Link
                                      </Button>
                                      <Button
                                          variant="outlined"
                                          color="error"
                                          sx={actionButtonSx}
                                          onClick={() => handleUnlink(rowData)}
                                          disabled={!!rowActionLoading[`unlink-${rowData.product_id}`]}
                                      >
                                          Unlink
                                      </Button>
                                  </ButtonGroup>
                              );
                          }

                          return (
                              <ButtonGroup>
                                  <Button
                                      variant="outlined"
                                      color="primary"
                                      sx={actionButtonSx}
                                      onClick={() => {
                                          setEditingCode(rowData.code);
                                          setSelectedLinkProduct(null);
                                      }}
                                  >
                                      Link
                                  </Button>
                                  <Button
                                      variant="outlined"
                                      color="primary"
                                      sx={actionButtonSx}
                                      onClick={() => {
                                          setSelectedMillionRow(rowData);
                                          setOpenCreate(true);
                                      }}
                                  >
                                      Create Product
                                  </Button>
                              </ButtonGroup>
                          );
                      },
                  },
              ]
            : [
                  { title: "CRM Product", field: "product_name" },
                  { title: "CRM Description", field: "product_description" },
                  {
                      title: "CRM Price",
                      field: "price",
                      cellStyle: { width: 130, maxWidth: 130 },
                      headerStyle: { width: 130, maxWidth: 130 },
                      render: (rowData) => (rowData.price === null || rowData.price === undefined ? "-" : Number(rowData.price).toFixed(2)),
                  },
                  {
                      title: "Link Status",
                      field: "million_product_code",
                      cellStyle: { width: 120, maxWidth: 120 },
                      headerStyle: { width: 120, maxWidth: 120 },
                      render: (rowData) =>
                          rowData.million_product_code || rowData.code ? <Chip label="LINKED" color="success" size="small" /> : <Chip label="UNLINKED" color="default" size="small" />,
                  },
                  { title: "Million Code", field: "code", render: (rowData) => rowData.code || rowData.million_product_code || "-" },
                  { title: "Million Description", field: "description", render: (rowData) => rowData.description || "-" },
                  {
                      title: "Million Price",
                      field: "million_price",
                      cellStyle: { width: 130, maxWidth: 130 },
                      headerStyle: { width: 130, maxWidth: 130 },
                      render: (rowData) => (rowData.million_price === null || rowData.million_price === undefined ? "-" : Number(rowData.million_price).toFixed(2)),
                  },
                  {
                      title: "Updated At",
                      field: "updated_at",
                      cellStyle: { width: 190, maxWidth: 190 },
                      headerStyle: { width: 190, maxWidth: 190 },
                      render: (rowData) => (rowData.updated_at ? moment(rowData.updated_at).format("YYYY-MM-DD HH:mm:ss") : "-"),
                  },
                  {
                      title: "Actions",
                      sorting: false,
                      filtering: false,
                      render: (rowData) => {
                          if (editingCrmProductId === rowData.product_id) {
                              return (
                                  <Box sx={{ minWidth: 420, display: "flex", gap: 1, alignItems: "center" }}>
                                      <Autocomplete
                                          size="small"
                                          options={millionOptions}
                                          value={selectedMillionLink}
                                          onChange={(event, value) => setSelectedMillionLink(value)}
                                          onInputChange={(event, inputValue) => setMillionSearch(inputValue)}
                                          isOptionEqualToValue={(option, value) => option.code === value?.code}
                                          getOptionLabel={(option) => `${option?.code || ""}${option?.description ? ` - ${option.description}` : ""}`}
                                          renderOption={(props, option) => (
                                              <li {...props} key={`${option.code}-${option.updated_at || ""}`}>
                                                  {option.code} - {option.description}
                                              </li>
                                          )}
                                          renderInput={(params) => <TextField {...params} label="Select Million Product" placeholder="Search million product" />}
                                          fullWidth
                                      />
                                      <Button
                                          variant="contained"
                                          color="primary"
                                          sx={{ color: "#fff" }}
                                          onClick={() => handleSaveLinkFromCrm(rowData)}
                                          disabled={!selectedMillionLink || !!rowActionLoading[`link-crm-${rowData.product_id}`]}
                                      >
                                          Save
                                      </Button>
                                      <Button
                                          variant="contained"
                                          color="error"
                                          sx={{ color: "#fff" }}
                                          onClick={() => {
                                              setEditingCrmProductId(null);
                                              setSelectedMillionLink(null);
                                              setMillionSearch("");
                                          }}
                                      >
                                          Cancel
                                      </Button>
                                  </Box>
                              );
                          }

                          if (rowData.million_product_code || rowData.code) {
                              return (
                                  <ButtonGroup>
                                      <Button
                                          variant="outlined"
                                          color="primary"
                                          sx={actionButtonSx}
                                          onClick={() => {
                                              setEditingCrmProductId(rowData.product_id);
                                              setSelectedMillionLink(
                                                  rowData.code
                                                      ? {
                                                            code: rowData.code,
                                                            description: rowData.description,
                                                            price: rowData.million_price,
                                                        }
                                                      : null
                                              );
                                              setMillionSearch(rowData.code || rowData.million_product_code || "");
                                          }}
                                      >
                                          Change Link
                                      </Button>
                                      <Button
                                          variant="outlined"
                                          color="error"
                                          sx={actionButtonSx}
                                          onClick={() => handleUnlink(rowData)}
                                          disabled={!!rowActionLoading[`unlink-${rowData.product_id}`]}
                                      >
                                          Unlink
                                      </Button>
                                  </ButtonGroup>
                              );
                          }

                          return (
                              <Button
                                  variant="outlined"
                                  color="primary"
                                  sx={actionButtonSx}
                                  onClick={() => {
                                      setEditingCrmProductId(rowData.product_id);
                                      setSelectedMillionLink(null);
                                      setMillionSearch("");
                                  }}
                              >
                                  Link
                              </Button>
                          );
                      },
                  },
              ];

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
                <Card sx={{ mb: 2 }}>
                    <CardHeader
                        title={<Typography component="h1" variant="h5" color="primary" fontWeight="bold">Million Product Mapping</Typography>}
                        subheader="Map Million mirrored products to CRM products and monitor sync health."
                    />
                    <CardContent>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                                    {syncStatusChip}
                                    <Chip label={`Million Total: ${syncStatus.millionProductTotal || 0}`} size="small" />
                                    <Chip label={`CRM Total: ${syncStatus.crmProductTotal || 0}`} size="small" />
                                    <Chip label={`Linked Total: ${syncStatus.linkedProductTotal || 0}`} size="small" />
                                    <Chip
                                        label={`Last Sync: ${syncStatus?.state?.last_successful_sync_at ? moment(syncStatus.state.last_successful_sync_at).format("YYYY-MM-DD HH:mm:ss") : "-"}`}
                                        size="small"
                                    />
                                    <Chip
                                        label={`Last Attempt: ${syncStatus?.state?.last_attempted_sync_at ? moment(syncStatus.state.last_attempted_sync_at).format("YYYY-MM-DD HH:mm:ss") : "-"}`}
                                        size="small"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
                                <Box sx={{ display: "inline-flex", flexDirection: "column", gap: 1, alignItems: { xs: "flex-start", md: "flex-end" } }}>
                                    <ButtonGroup>
                                        <Button variant={viewMode === "million" ? "contained" : "outlined"} color="primary" onClick={() => {
                                            setViewMode("million");
                                            setPage(0);
                                            setEditingCrmProductId(null);
                                            setEditingCode(null);
                                        }}>
                                            View by Million
                                        </Button>
                                        <Button variant={viewMode === "crm" ? "contained" : "outlined"} color="primary" onClick={() => {
                                            setViewMode("crm");
                                            setPage(0);
                                            setEditingCrmProductId(null);
                                            setEditingCode(null);
                                            setOpenCreate(false);
                                        }}>
                                            View by CRM
                                        </Button>
                                    </ButtonGroup>
                                    <ButtonGroup>
                                        <Button variant="outlined" color="primary" onClick={refreshAll}>
                                            Refresh
                                        </Button>
                                        <Button variant="outlined" color="primary" onClick={handleAutoPair} disabled={autoPairLoading}>
                                            Run Auto-Pair
                                        </Button>
                                    </ButtonGroup>
                                </Box>
                            </Grid>
                            {syncStatus?.state?.last_error && (
                                <Grid item xs={12}>
                                    <Alert severity="warning" variant="outlined">
                                        {syncStatus.state.last_error}
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>

                <MaterialTable
                    title="Million Product Mapping"
                    icons={Icons}
                    columns={columns}
                    data={data}
                    page={page}
                    totalCount={count}
                    style={{ padding: "24px" }}
                    options={{
                        pageSize: perPage,
                        pageSizeOptions: [10, 20, 30, 50],
                        search: false,
                        filtering: false,
                        sorting: false,
                        headerStyle: {
                            fontWeight: "bold",
                            backgroundColor: "#f1f1f1",
                            color: "#212122",
                        },
                        maxBodyHeight: "600px",
                        paginationType: "stepped",
                        padding: "dense",
                        draggable: false,
                        actionsColumnIndex: -1,
                    }}
                    localization={{
                        pagination: {
                            labelRowsPerPage: "",
                            labelDisplayedRows: "{from}-{to} of {count}",
                            labelRowsSelect: "rows",
                        },
                        toolbar: {
                            nRowsSelected: "{0} row(s) selected",
                        },
                        header: {
                            actions: "Actions",
                        },
                        body: {
                            emptyDataSourceMessage: "No data to display",
                        },
                    }}
                    onChangePage={(nextPage) => setPage(nextPage)}
                    onChangeRowsPerPage={(rowsPerPage) => setPerPage(rowsPerPage)}
                    components={{
                        Toolbar: () => (
                            <Box sx={{ pb: 2.5 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Manage By"
                                            variant="outlined"
                                            name="country_id"
                                            value={filter.country_id}
                                            onChange={handleFilterChange}
                                            size="small"
                                        >
                                            {countryOptions.map((country) => (
                                                <MenuItem key={country.country_id} value={country.country_id}>
                                                    {country.country_name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <TextField
                                            autoComplete="off"
                                            fullWidth
                                            label={viewMode === "million" ? "Search code or description" : "Search CRM name or description"}
                                            name="search"
                                            variant="outlined"
                                            size="small"
                                            autoFocus={searchInputRef.current === document.activeElement}
                                            value={filter.search}
                                            onChange={handleFilterChange}
                                            inputRef={searchInputRef}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Status"
                                            variant="outlined"
                                            name="status"
                                            value={filter.status}
                                            onChange={handleFilterChange}
                                            size="small"
                                        >
                                            <MenuItem value="all">All</MenuItem>
                                            <MenuItem value="linked">Linked</MenuItem>
                                            <MenuItem value="unlinked">Unlinked</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="error"
                                            sx={{ color: "#ffffff", mt: "2px" }}
                                            onClick={() => {
                                                setPage(0);
                                                setFilter((prev) => ({ ...prev, search: "", status: "all" }));
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        ),
                    }}
                />

                {viewMode === "million" && (
                    <CreateProductForm
                        open={openCreate}
                        handleClose={() => {
                            setOpenCreate(false);
                            setSelectedMillionRow(null);
                        }}
                        fetchData={refreshAll}
                        userCountry={userCountry}
                        initialData={
                            selectedMillionRow
                                ? {
                                      product_name: selectedMillionRow.code || "",
                                      product_description: selectedMillionRow.description || "",
                                      million_product_code: selectedMillionRow.code || "",
                                      price: selectedMillionRow.price,
                                  }
                                : null
                        }
                    />
                )}
            </Container>
        </div>
    );
};
